import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, CATEGORY_META, fmtDate, isOverdue } from "@/lib/constants";
import Avatar from "@/components/Avatar";
import TaskDialog from "@/components/TaskDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ChevronLeft, Paperclip, Flag, Trash2, ChevronDown, ListChecks, Pencil, Check, X, FileDown, List, LayoutGrid, Calendar as CalendarIcon, GanttChart, DollarSign, FileText, MapPin, Contact, Briefcase, ClipboardList, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { printProject } from "@/lib/printProject";
import { KanbanBoard } from "@/components/views/KanbanBoard";
import { CalendarView } from "@/components/views/CalendarView";
import { TimelineView } from "@/components/views/TimelineView";
import FeesTab from "@/components/FeesTab";
import SchedulesTab from "@/components/schedules/SchedulesTab";
import DocumentChecklist from "@/components/documents/DocumentChecklist";
import QuickAssign from "@/components/task/QuickAssign";
import ProjectClients from "@/components/ProjectClients";
import ProjectConsultants from "@/components/project/ProjectConsultants";
import FilesManager from "@/components/files/FilesManager";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const GRID = "grid grid-cols-[20px_minmax(0,1fr)_110px_90px_80px_80px_90px_34px] gap-3 items-center";

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkOpened = useRef(false);
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.can_manage;
  const isAdmin = user?.role === "admin";
  const canViewClients = user?.role === "admin" || user?.can_view_clients;
  const canViewFees = user?.role === "admin" || user?.can_view_fees;

  const [project, setProject] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [editDesc, setEditDesc] = useState(false);
  const [descVal, setDescVal] = useState("");
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [editDetails, setEditDetails] = useState(false);
  const [addrVal, setAddrVal] = useState("");
  const [view, setView] = useState("list");

  const reloadChecklist = useCallback(async () => {
    const r = await api.get(`/projects/${projectId}/checklist`);
    setChecklist(r.data);
    return r.data;
  }, [projectId]);

  const enableChecklist = async () => {
    try {
      await api.put(`/projects/${projectId}/checklist/enable`, { enabled: true });
      await reloadChecklist();
      setView("documents");
      toast.success("Document checklist enabled");
    } catch (e) { toast.error(apiErr(e, "Could not enable checklist")); }
  };

  const saveDetails = async () => {
    try {
      const r = await api.patch(`/projects/${projectId}`, { address: addrVal });
      setProject(r.data);
      setEditDetails(false);
      toast.success("Project details updated");
    } catch (e) { toast.error(apiErr(e, "Failed to update details")); }
  };

  const changeStatus = async (taskId, status) => {
    try {
      await api.patch(`/tasks/${taskId}`, { status });
      load();
    } catch (e) {
      toast.error(apiErr(e, "Could not update status"));
    }
  };

  const saveDesc = async () => {
    try {
      await api.patch(`/projects/${projectId}`, { description: descVal });
      setProject((p) => ({ ...p, description: descVal }));
      setEditDesc(false);
      toast.success("Description updated");
    } catch (e) { toast.error("Failed to update description"); }
  };
  const saveName = async () => {
    const v = nameVal.trim();
    if (!v) { toast.error("Project name can't be empty"); return; }
    if (v === project.name) { setEditName(false); return; }
    try {
      await api.patch(`/projects/${projectId}`, { name: v });
      setProject((p) => ({ ...p, name: v }));
      setEditName(false);
      toast.success("Project name updated");
    } catch (e) { toast.error(apiErr(e, "Failed to rename project")); }
  };
  const [collapsed, setCollapsed] = useState({});

  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m])), [members]);

  const load = useCallback(async () => {
    try {
      const cfg = { timeout: 20000 };
      const [p, t, u, cl] = await Promise.all([
        api.get(`/projects/${projectId}`, cfg),
        api.get(`/projects/${projectId}/tasks`, cfg),
        api.get(`/users`, cfg),
        api.get(`/projects/${projectId}/checklist`, cfg),
      ]);
      setProject(p.data);
      setTasks(t.data);
      setMembers(u.data);
      setChecklist(cl.data);
      setLoadError(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = apiErr(e);
      setLoadError(
        status === 404 ? "Project not found — it may have been deleted."
          : detail || (e?.code === "ECONNABORTED" ? "The server took too long to respond."
            : `Could not load this project (${status ? `error ${status}` : "network error"}).`)
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { setLoading(true); setLoadError(null); load(); }, [projectId, load]);

  const grouped = useMemo(() => {
    const g = {};
    STATUS_ORDER.forEach((s) => (g[s] = []));
    tasks.forEach((t) => { (g[t.status] = g[t.status] || []).push(t); });
    return g;
  }, [tasks]);

  const openNew = () => { setActiveTask(null); setDialogOpen(true); };
  const openTask = (t) => { setActiveTask(t); setDialogOpen(true); };

  // Deep link: /project/:id?task=<task_id> auto-opens that task once tasks have loaded.
  useEffect(() => {
    const tid = searchParams.get("task");
    if (!tid || deepLinkOpened.current || !tasks.length) return;
    const t = tasks.find((x) => x.task_id === tid);
    if (t) { deepLinkOpened.current = true; setActiveTask(t); setDialogOpen(true); }
    // deepLinkOpened is a ref and set* are stable; intentionally keyed on tasks/searchParams only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchParams]);

  const handleDialogOpenChange = (o) => {
    setDialogOpen(o);
    if (!o && searchParams.get("task")) {
      searchParams.delete("task");
      setSearchParams(searchParams, { replace: true });
      deepLinkOpened.current = false;
    }
  };

  const exportPdf = () => {
    const ok = printProject({ project, tasks, memberMap });
    if (ok === false) toast.error("Allow pop-ups to export the PDF");
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (loading) {
    return <div className="p-10"><div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (loadError || !project) {
    return (
      <div className="p-10 max-w-lg" data-testid="project-load-error">
        <h2 className="font-heading text-2xl font-black tracking-tight mb-2">Couldn&apos;t load this project</h2>
        <p className="text-sm text-zinc-500 mb-6">{loadError || "Unknown error."}</p>
        <div className="flex gap-3">
          <button data-testid="project-load-retry" onClick={() => { setLoading(true); setLoadError(null); load(); }}
            className="px-4 py-2 bg-zinc-900 text-white text-sm font-semibold rounded-sm hover:bg-zinc-700 transition-colors">Retry</button>
          <button data-testid="project-load-back" onClick={() => navigate("/")}
            className="px-4 py-2 border border-zinc-300 text-sm font-semibold rounded-sm hover:bg-zinc-100 transition-colors">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const meta = CATEGORY_META[project.category];

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <button onClick={() => navigate(`/folder/${project.category}/builder/${project.builder_id || "none"}`)}
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-4" data-testid="back-button">
        <ChevronLeft className="h-4 w-4" /> {meta?.label} · {meta?.sub}{project.builder ? ` · ${project.builder}` : ""}
      </button>

      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 gap-4">
        <div>
          {editName ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="project-name-edit">
              <Input
                value={nameVal}
                onChange={(e) => setNameVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditName(false); }}
                className="rounded-sm font-heading text-2xl sm:text-3xl font-black tracking-tighter h-auto py-1 max-w-full w-full sm:w-[28rem]"
                data-testid="project-name-input" autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveName} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm h-8 gap-1" data-testid="save-name"><Check className="h-3.5 w-3.5" /> Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditName(false)} className="rounded-sm h-8 gap-1"><X className="h-3.5 w-3.5" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 group/name">
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter break-words" data-testid="project-name">{project.name}</h1>
              {canManage && (
                <button onClick={() => { setNameVal(project.name || ""); setEditName(true); }}
                  className="text-zinc-400 hover:text-zinc-900 sm:opacity-0 sm:group-hover/name:opacity-100 transition-opacity shrink-0 mt-2" data-testid="edit-name" aria-label="Rename project">
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          {editDesc ? (
            <div className="mt-2 max-w-xl">
              <Textarea value={descVal} onChange={(e) => setDescVal(e.target.value)} className="rounded-sm text-sm" placeholder="Describe this project…" data-testid="project-desc-input" autoFocus />
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={saveDesc} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm h-8 gap-1" data-testid="save-desc"><Check className="h-3.5 w-3.5" /> Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditDesc(false)} className="rounded-sm h-8 gap-1"><X className="h-3.5 w-3.5" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 max-w-xl flex items-start gap-2 group">
              <p className="text-zinc-500 text-sm" data-testid="project-description">{project.description || (canManage ? "Add a description…" : "")}</p>
              {canManage && (
                <button onClick={() => { setDescVal(project.description || ""); setEditDesc(true); }}
                  className="text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" data-testid="edit-desc">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <p className="text-zinc-400 mt-2 text-xs uppercase tracking-wider">{tasks.length} task{tasks.length !== 1 ? "s" : ""}{String(project.category).startsWith("interior_") ? " · 2 schedules" : ""}</p>

          {editDetails ? (
            <div className="mt-3 max-w-xl space-y-2" data-testid="project-details-edit">
              <Input value={addrVal} onChange={(e) => setAddrVal(e.target.value)} placeholder="Site address, e.g. 38 Richmond Street, Earlwood NSW 2206"
                className="rounded-sm text-sm h-9" data-testid="project-address-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveDetails} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm h-8 gap-1" data-testid="save-details"><Check className="h-3.5 w-3.5" /> Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditDetails(false)} className="rounded-sm h-8 gap-1"><X className="h-3.5 w-3.5" /> Cancel</Button>
              </div>
            </div>
          ) : (project.address || project.client || canManage) && (
            <div className="mt-2 flex items-center gap-3 group text-xs text-zinc-500 flex-wrap" data-testid="project-details">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {project.address || (canManage ? "Add site address" : "—")}</span>
              {canViewClients && <span>Client: <strong className="text-zinc-700" data-testid="project-primary-client">{project.client || "—"}</strong></span>}
              {canManage && (
                <button onClick={() => { setAddrVal(project.address || ""); setEditDetails(true); }}
                  className="text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="edit-details">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Button data-testid="export-pdf-button" variant="outline" onClick={exportPdf} className="rounded-sm gap-2">
            <FileDown className="h-4 w-4" /> Export PDF
          </Button>
          {canManage && !checklist?.enabled && (
            <Button data-testid="enable-checklist-button" variant="outline" onClick={enableChecklist} className="rounded-sm gap-2">
              <FileText className="h-4 w-4" /> Doc Checklist
            </Button>
          )}
          {canManage && (
            <Button data-testid="add-task-button" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2">
              <Plus className="h-4 w-4" /> Add Task
            </Button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-1 mb-4 bg-zinc-100 rounded-sm p-1 w-fit max-w-full overflow-x-auto no-scrollbar" data-testid="view-switcher">
        {[
          { k: "list", label: "List", Icon: List },
          { k: "board", label: "Board", Icon: LayoutGrid },
          { k: "calendar", label: "Calendar", Icon: CalendarIcon },
          { k: "timeline", label: "Timeline", Icon: GanttChart },
          ...(canViewClients ? [{ k: "clients", label: "Clients", Icon: Contact }] : []),
          ...(canViewFees ? [{ k: "fees", label: "Fees", Icon: DollarSign }] : []),
          ...(String(project.category).startsWith("interior_") ? [{ k: "schedules", label: "Schedules", Icon: ClipboardList }] : []),
          ...(checklist?.enabled ? [{ k: "documents", label: "Documents", Icon: FileText }] : []),
          { k: "consultants", label: "Consultants", Icon: Briefcase },
          { k: "files", label: "Files", Icon: HardDrive },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setView(k)} data-testid={`view-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors shrink-0 whitespace-nowrap ${view === k ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-900"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {view === "clients" ? (
        <ProjectClients projectId={projectId} project={project} canManage={canManage} onChange={(p) => setProject(p)} />
      ) : view === "files" ? (
        <FilesManager projectId={projectId} />
      ) : view === "consultants" ? (
        <ProjectConsultants projectId={projectId} project={project} members={members} canManage={canManage} />
      ) : view === "documents" ? (
        <DocumentChecklist projectId={projectId} project={project} checklist={checklist} canManage={canManage} onReload={reloadChecklist} members={members} />
      ) : view === "fees" && canViewFees ? (
        <FeesTab projectId={projectId} />
      ) : view === "schedules" && String(project.category).startsWith("interior_") ? (
        <SchedulesTab project={project} canManage={canManage} />
      ) : tasks.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center">
          <h3 className="font-heading text-xl font-bold mb-2">No tasks yet</h3>
          <p className="text-zinc-500 text-sm">{canManage ? "Add your first task to build out this project's template." : "No tasks have been added to this project."}</p>
        </div>
      ) : (
        <>
          {view === "list" && (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <div className="min-w-[720px]">
          {/* header */}
          <div className={`${GRID} px-4 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500`}>
            <span></span>
            <span>Task</span>
            <span>Assigned To</span>
            <span>By</span>
            <span>Start</span>
            <span>End</span>
            <span>Priority</span>
            <span></span>
          </div>

          {STATUS_ORDER.map((s) => {
            const list = grouped[s];
            if (!list || list.length === 0) return null;
            const isCol = collapsed[s];
            return (
              <div key={s}>
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [s]: !c[s] }))}
                  className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-zinc-100"
                  style={{ background: STATUS_META[s].bg }}
                  data-testid={`group-header-${s}`}>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isCol ? "-rotate-90" : ""}`} style={{ color: STATUS_META[s].color }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: STATUS_META[s].color }}>{STATUS_META[s].label}</span>
                  <span className="text-xs font-semibold" style={{ color: STATUS_META[s].color }}>{list.length}</span>
                </button>

                {!isCol && list.map((t) => {
                  const by = memberMap[t.assigned_by];
                  return (
                    <div key={t.task_id}
                      onClick={() => openTask(t)}
                      className={`${GRID} px-4 py-3 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer group`}
                      data-testid={`task-row-${t.task_id}`}>
                      <span className="h-3.5 w-3.5 rounded-full border-2" style={{ borderColor: STATUS_META[t.status].color, background: t.status === "completed" ? STATUS_META[t.status].color : "transparent" }} />
                      <span className="text-sm font-medium truncate flex items-center gap-2">
                        {t.name}
                        {t.attachments?.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400"><Paperclip className="h-3 w-3" />{t.attachments.length}</span>
                        )}
                        {t.checklist?.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400"><ListChecks className="h-3 w-3" />{t.checklist.filter((c) => c.done).length}/{t.checklist.length}</span>
                        )}
                      </span>
                      <span className="flex -space-x-1.5" onClick={(e) => e.stopPropagation()}>
                        <QuickAssign task={t} members={members} memberMap={memberMap} onSaved={load} />
                      </span>
                      <span>{by ? <Avatar id={by.user_id} name={by.name} size={24} /> : <span className="text-xs text-zinc-300">—</span>}</span>
                      <span className="text-xs text-zinc-600">{fmtDate(t.start_date)}</span>
                      <span className={`text-xs ${isOverdue(t) ? "text-rose-600 font-semibold" : "text-zinc-600"}`}>{fmtDate(t.end_date)}</span>
                      <span>
                        {t.priority && t.priority !== "none"
                          ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: PRIORITY_META[t.priority].color }}><Flag className="h-3 w-3" fill={PRIORITY_META[t.priority].color} />{PRIORITY_META[t.priority].label}</span>
                          : <Flag className="h-3.5 w-3.5 text-zinc-200" />}
                      </span>
                      <span className="flex justify-end">
                        {canManage && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 transition-opacity" data-testid={`delete-task-${t.task_id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm" onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                                <AlertDialogDescription>&ldquo;{t.name}&rdquo; will be permanently removed.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => deleteTask(t.task_id)}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {canManage && (
            <button onClick={openNew} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-400 hover:text-zinc-900 transition-colors w-full" data-testid="add-task-inline">
              <Plus className="h-4 w-4" /> Add Task
            </button>
          )}
          </div>
        </div>
          )}

          {view === "board" && (
            <KanbanBoard tasks={tasks} memberMap={memberMap} members={members} onOpenTask={openTask} onStatusChange={changeStatus} canManage={canManage} onSaved={load} />
          )}
          {view === "calendar" && <CalendarView tasks={tasks} onOpenTask={openTask} />}
          {view === "timeline" && <TimelineView tasks={tasks} onOpenTask={openTask} />}
        </>
      )}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        task={activeTask}
        projectId={projectId}
        projectName={project.name}
        builderName={project.builder}
        members={members}
        canManage={canManage}
        currentUser={user}
        onSaved={load}
      />
    </div>
  );
}
